import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  styled,
  Button,
  Typography,
  Grid,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import * as HrActions from '../../../store/actions/hr.action';
import { getUser } from '../../../store/selector/auth.selector';
import { getHrState } from '../../../store/selector/hr.selector';
import DynamicCrumbs from '../../../shared/components/ui/dashboard-layout/dynamic-crumbs';
import DynamicTabLink from '../../../shared/components/ui/dynamic-tabs/dynamic-tab-link';
import EditHRInfomartionModal from '../../../shared/components/ui/modal/edit-hr-information-modal';
import OptionsMenu from '../../../shared/components/ui/menu/options-menu';
import { IDynamicTabLink } from '../../../shared/interfaces/dynamic-tab.interface';
import { IEditHrInformationModal } from '../../../shared/interfaces/modal.interface';
import { IOptionsButton } from '../../../shared/interfaces/options.interface';
import { hrInformationOptions } from '../../../shared/constants/hr-information-options';
import { HR_BP_TABS_CONSTANT } from '../../../shared/constants/hrbp-tabs.constant';
import { HR_TABS_CONSTANT } from '../../../shared/constants/hr-tabs.constant';
import { HR_VIEW_ONLY_TABS_CONSTANT } from '../../../shared/constants/hr-view-only.constant';

const styles = {
  container: {
    background: '#fff',
    p: 4,
  },

  header: {
    display: 'flex',
    flexDirection: { sm: 'row', xs: 'column' },
    justifyContent: 'space-between',
    alignItems: { sm: 'center', xs: 'flex-start' },
    mb: 3,
  },

  downloadOptionsButton: {
    padding: '0.5rem 1.5rem',
    borderRadius: '0.5rem',
    width: { sm: 'auto', xs: '100%' },
    mt: { sm: 0, xs: '2rem' },
  },
};

const Data = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
  fontSize: '1rem',
}));

const HrInformation = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const {
    selected: { application },
  } = useSelector(getHrState);
  const { slug } = useParams<string>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [options, setOptions] = useState<Array<IOptionsButton> | undefined>();
  const [tabItems, setTabItems] = useState<IDynamicTabLink[]>();
  const [userType, setUserType] = useState<string>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const onModalAction = (e: string, data?: IEditHrInformationModal) => {
    if (e === 'update') {
      if (slug && data) dispatch(HrActions.HrEditApplication(slug, data));

      setIsModalOpen(false);
    } else {
      setIsModalOpen(false);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  useEffect(() => {
    const { groups } = user;

    switch (true) {
      case groups.includes('hr-bp'):
        setTabItems(HR_BP_TABS_CONSTANT);
        break;

      case groups.includes('hr-view-only'):
        setTabItems(HR_VIEW_ONLY_TABS_CONSTANT);
        break;

      default:
        setTabItems(HR_TABS_CONSTANT);
        break;
    }
  }, [user]);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (slug) dispatch(HrActions.HrFetchApplication(slug));
  }, []);

  useEffect(() => {
    if (application) {
      if (user.groups.includes('hr-bp')) {
        setUserType('hr-bp');
      } else if (application.status === 'finalized') {
        setUserType('hrFinalized');
      } else {
        setUserType('hr');
      }
    }
  }, [application]);

  useEffect(() => {
    if (userType) {
      setOptions(hrInformationOptions(userType, slug, openModal));
    }
  }, [userType]);

  return (
    <>
      <Box sx={styles.header}>
        <Box>
          <DynamicCrumbs />

          <Typography sx={{ fontSize: '1.5rem' }}>
            {application?.lastname}, {application?.firstname}, {application?.middlename} -{' '}
            {application?.code}
          </Typography>
        </Box>

        <Button
          variant='contained'
          sx={styles.downloadOptionsButton}
          disableElevation
          onClick={handleClick}
          endIcon={<KeyboardArrowDownIcon />}
        >
          Actions
        </Button>

        <OptionsMenu
          open={open}
          handleClose={handleClose}
          anchorEl={anchorEl}
          optionItems={options}
        />
      </Box>

      {tabItems && <DynamicTabLink tabs={tabItems} />}

      <Box sx={{ my: 2 }}>
        {application && (
          <Grid container sx={styles.container}>
            <Grid
              item
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', mb: '2rem' }}
              gap={'0.2rem'}
            >
              <Typography color='primary' sx={{ fontSize: '1.5rem', mb: { sm: 0, xs: '1rem' } }}>
                Personal Information
              </Typography>

              {!isMobile && <Divider sx={{ mb: '1rem', width: '25rem' }} />}

              <Data>
                Name: {application?.lastname}, {application?.firstname}, {application?.middlename}
              </Data>

              <Data>Birthdate: {application?.birthdate} </Data>

              <Data>Email Address: {application?.email_address}</Data>

              <Data>Mobile Number: {application?.mobile_no} </Data>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', mb: '2rem' }}
              gap={'0.2rem'}
            >
              <Typography color='primary' sx={{ fontSize: '1.5rem', mb: { sm: 0, xs: '1rem' } }}>
                Employment Information
              </Typography>

              {!isMobile && <Divider sx={{ mb: '1rem', width: '25rem' }} />}

              <Data>Employee Code: {application?.code}</Data>

              <Data>Affiliate Description: {application?.company}</Data>

              <Data>Department: {application?.department}</Data>

              <Data>Position: {application?.position}</Data>

              <Data>Base Assignment: {application?.base_of_assignment}</Data>

              {application?.remarks && <Data>Employee Remarks: {application?.remarks}</Data>}
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', mb: '1rem' }}
              gap={'0.2rem'}
            >
              <Typography color='primary' sx={{ fontSize: '1.5rem', mb: { sm: 0, xs: '1rem' } }}>
                Clearance Information
              </Typography>

              {!isMobile && <Divider sx={{ mb: '1rem', width: '25rem' }} />}

              <Data>Date Hired: {application?.hiring_date}</Data>

              <Data>Date of Clearance Application: {application?.clearance_application_date}</Data>

              <Data>Last Working Day: {application?.last_working_day}</Data>

              <Data>Effective Date of Separation: {application?.effective_separation_date}</Data>

              {application?.completion_date && (
                <Data>Completion Date: {application.completion_date}</Data>
              )}

              <Data sx={{ width: { md: '50%', xs: '100%' } }}>
                Reason for Separation: {application?.reason}
              </Data>

              {application.reason === 'Dissatisfaction at Work' && (
                <Data sx={{ width: { md: '50%', xs: '100%' } }}>
                  Dissatisfaction Cause: {application?.dissatisfaction_cause}
                </Data>
              )}

              {application.reason === 'End of Contract' && (
                <Data sx={{ width: { md: '50%', xs: '100%' } }}>
                  End of Contract Cause: {application?.end_contract_cause}
                </Data>
              )}

              {application?.supervisor_remarks && (
                <Data>Supervisor Remarks: {application?.supervisor_remarks}</Data>
              )}
            </Grid>
          </Grid>
        )}
      </Box>

      <EditHRInfomartionModal
        modalOpen={isModalOpen}
        onClickModal={(e, data) => onModalAction(e, data)}
      />
    </>
  );
};
export default HrInformation;
